import type { RouteParams } from 'vue-router';
import type { ViewGroup, NavigationResult } from '@/utils/types';
import { SafeNavigation, NavigationFallbacks } from '@/utils/safeNavigation';

export function useNavigationHelpers() {
  const getPrevGroupNavigation = (
    prevGroup: ViewGroup | null | undefined,
    params: RouteParams
  ): NavigationResult => {
    return SafeNavigation.getSafeRoute(
      prevGroup,
      'CalculationViewItems',
      params,
      'Wstecz',
      {
        name: NavigationFallbacks.SEGMENT_SELECTION,
        params,
        title: 'Wstecz',
      }
    );
  };

  const getNextGroupNavigation = (
    nextGroup: ViewGroup | null | undefined,
    isNextStepsNotRequired: boolean,
    params: RouteParams
  ): NavigationResult => {
    if (!nextGroup || isNextStepsNotRequired) {
      return {
        route: {
          name: NavigationFallbacks.CALCULATION_SUMMARY,
          params,
        },
        title: 'Podsumowanie',
      };
    }

    return SafeNavigation.getSafeRoute(
      nextGroup,
      'CalculationViewItems',
      params,
      'Dalej',
      {
        name: NavigationFallbacks.CALCULATION_SUMMARY,
        params,
        title: 'Podsumowanie',
      }
    );
  };

  const getNextElementNavigation = (
    nextGroup: ViewGroup | null | undefined,
    isNextStepsNotRequired: boolean,
    params: RouteParams
  ): NavigationResult => {
    if (!isNextStepsNotRequired || !nextGroup) {
      return null;
    }

    const result = SafeNavigation.createNavigation(
      nextGroup,
      'CalculationViewItems',
      params,
      'Dodaj kolejny element'
    );

    return result.success ? result.data : null;
  };

  const getNextNotSkipableGroupNavigation = (
    nextGroup: ViewGroup | null | undefined,
    nextNotSkipableGroup: ViewGroup | null | undefined,
    params: RouteParams
  ): NavigationResult => {
    if (!nextGroup?.is_skipable || !nextNotSkipableGroup) {
      return null;
    }

    const result = SafeNavigation.createNavigation(
      nextNotSkipableGroup,
      'CalculationViewItems',
      params,
      'Dalej'
    );

    return result.success ? result.data : null;
  };

  const getStepperNavigation = (
    group: ViewGroup | null | undefined,
    params: RouteParams
  ): { shouldNavigate: boolean; route?: any } => {
    if (!SafeNavigation.validateGroupHasViews(group)) {
      console.warn(`Group ${group?.id || 'unknown'} has no views available for navigation`);
      return { shouldNavigate: false };
    }

    const firstView = SafeNavigation.getFirstView(group);
    return {
      shouldNavigate: true,
      route: {
        name: 'CalculationViewItems',
        params: {
          ...params,
          groupId: group?.id,
          viewId: firstView!.id,
        },
      },
    };
  };

  const getInitialCalculationNavigation = (
    segments: any[] | null | undefined,
    params: RouteParams
  ): NavigationResult => {
    if (!segments?.length) {
      return {
        route: {
          name: NavigationFallbacks.SEGMENT_SELECTION,
          params,
        },
        title: 'Wybierz segment',
      };
    }

    const firstSegment = segments[0];
    const firstValidGroup = SafeNavigation.findFirstValidGroup(firstSegment?.view_groups);

    const result = SafeNavigation.createNavigation(
      firstValidGroup,
      'CalculationViewItems',
      {
        ...params,
        segmentId: firstSegment.id,
      },
      'Rozpocznij kalkulację'
    );

    if (result.success) {
      return result.data;
    }

    return {
      route: {
        name: NavigationFallbacks.SEGMENT_SELECTION,
        params,
      },
      title: 'Wybierz segment',
    };
  };

  return {
    getPrevGroupNavigation,
    getNextGroupNavigation,
    getNextElementNavigation,
    getNextNotSkipableGroupNavigation,
    getStepperNavigation,
    getInitialCalculationNavigation,
  };
}
